import React, {useState, useEffect, useMemo, useRef} from 'react'
import './ModalWide.css'
import './ModalPodEdit.css'
import ModalPodErrorLogs from './ModalPodErrorLogs'
import ModalPodConfig from './ModalPodConfig'
import { getCurrentUser } from '../utils/auth'
import { v4 as uuidv4 } from 'uuid';

const REMOTE_STATUS_POLL_MS = 5000;
const REMOTE_STATUS_EMPTY_POLL_LIMIT = 10;
const SIMPLE_REMOTE_CONTROL_COMMANDS = new Set(['start', 'stop', 'pause', 'logout']);
const REMOTE_CONTROL_MESSAGE_TIMEOUT_MS = 5000;
const REMOTE_STATUS_PILL_LABELS = {
    start_program: 'Start Program',
    complete: 'Complete',
    completed: 'Completed',
    log_out: 'Log Out',
    logout: 'Logout',
    exit_program: 'Exit Program',
    log_in: 'Log In',
    logged_out: 'Logged Out',
    logged_in: 'Logged In',
    idle: 'Idle',
    running: 'Running',
    paused: 'Paused',
    stop: 'Stop',
    stopped: 'Stopped',
    music: 'Music',
    fading_out: 'Fading Out',
};

const getRemoteStatusFlagClassName = (flag) => {
    switch (String(flag ?? '').trim().toLowerCase()) {
        case 'running':
            return 'remote-status-flag-running';
        case 'paused':
            return 'remote-status-flag-paused';
        case 'stopped':
            return 'remote-status-flag-stopped';
        default:
            return '';
    }
};

const formatDateForInput = (value) => {
    if (!value) {
        return '';
    }

    if (typeof value === 'string') {
        return value.slice(0, 10);
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return '';
    }

    return parsedDate.toISOString().slice(0, 10);
};

const formatInstallationDateFromSerialTag = (value) => {
    const serialTag = String(value ?? '').trim();
    const match = serialTag.match(/^(\d{2})(\d{2})(\d{4})-[A-Za-z0-9]{3}-[A-Za-z0-9]{4}$/);

    if (!match) {
        return '';
    }

    const [, month, day, year] = match;
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

    if (
        Number.isNaN(parsedDate.getTime())
        || parsedDate.getFullYear() !== Number(year)
        || parsedDate.getMonth() !== Number(month) - 1
        || parsedDate.getDate() !== Number(day)
    ) {
        return '';
    }

    return `${month}/${day}/${year}`;
};

const formatSecondsToMinutes = (value) => {
    const seconds = Number(value);
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '00:00';
    }

    const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
    const remainder = String(seconds % 60).padStart(2, '0');
    return `${minutes}:${remainder}`;
};

const parseRemoteTimestamp = (value) => {
    if (!value || typeof value !== 'string') {
        return null;
    }

    const normalizedValue = value.includes('T') ? value : value.replace(' ', 'T');
    const parsedDate = new Date(normalizedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const normalizeRemoteStatus = (payload) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload) || Object.keys(payload).length === 0) {
        return null;
    }

    const timestamp = parseRemoteTimestamp(payload.timestamp);
    if (!timestamp) {
        return null;
    }

    const activeFlags = [
        ['running', payload.running],
        ['paused', payload.paused],
        ['stopped', payload.stopped],
        ['completed', payload.completed],
    ]
        .filter(([, enabled]) => Boolean(enabled))
        .map(([label]) => REMOTE_STATUS_PILL_LABELS[label] ?? label);

    const eventKey = String(payload.event ?? '').trim();
    const eventLabel = REMOTE_STATUS_PILL_LABELS[eventKey] ?? eventKey;
    const shouldHideEventLabel = eventLabel === 'Stop' && activeFlags.includes('Stopped');

    if (eventLabel && !shouldHideEventLabel && !activeFlags.includes(eventLabel)) {
        activeFlags.unshift(eventLabel);
    }

    const clientId = payload.client_id ?? '';
    const clientFirstName = payload.client_first_name ?? '';
    const clientLastName = payload.client_last_name ?? '';
    const protocolName = payload.protocol_name ?? '';
    const hasClient = clientId !== '' && clientFirstName !== '' && clientLastName !== '';
    const shouldShowRemainingTime = Boolean(payload.running) || Boolean(payload.paused);
    const shouldCountdown = Boolean(payload.running) && !Boolean(payload.paused);

    return {
        event: eventKey,
        activeFlags,
        clientId,
        clientName: hasClient ? `${clientFirstName} ${clientLastName}` : '',
        showClient: hasClient,
        protocolName,
        showProtocolName: protocolName !== '',
        remainingTimeSec: shouldShowRemainingTime ? Math.max(0, Number(payload.total_time_remaining_sec) || 0) : 0,
        showRemainingTime: shouldShowRemainingTime,
        shouldCountdown,
        timestamp,
    };
};

const isRemoteStatusLater = (candidateStatus, currentStatus) => {
    if (!candidateStatus?.timestamp) {
        return false;
    }

    if (!currentStatus?.timestamp) {
        return true;
    }

    return candidateStatus.timestamp.getTime() > currentStatus.timestamp.getTime();
};

const buildProtocolOption = (item) => {
    const protocolId = Number(item?.protocol?.protocol_id ?? item?.protocol_id ?? item?.id ?? 0);
    const protocolName = String(item?.protocol?.name ?? item?.name ?? item?.description ?? '').trim();
    const protocolTypeDescription = String(
        item?.protocol?.protocol_type?.description
        ?? item?.protocol?.protocol_type_description
        ?? item?.protocol_type?.description
        ?? item?.protocol_type_description
        ?? ''
    ).trim();

    return {
        id: protocolId,
        description: protocolName,
        dropdownLabel: protocolTypeDescription ? `${protocolName} (${protocolTypeDescription})` : protocolName,
    };
};

const buildClientOption = (item) => {
    const clientId = Number(item?.id ?? item?.client_id ?? 0);
    const customerIdsArray = Array.isArray(item?.customer_ids) ? item.customer_ids : [];
    const nestedCustomerId = Array.isArray(item?.customers)
        ? Number(item.customers?.[0]?.customer_id ?? item.customers?.[0]?.id ?? 0)
        : Number(item?.customers?.customer_id ?? item?.customers?.id ?? item?.customer?.customer_id ?? item?.customer?.id ?? 0);
    const customerId = Number(item?.customer_id ?? item?.customer ?? customerIdsArray[0] ?? nestedCustomerId ?? 0);
    const firstName = String(item?.first_name ?? '').trim();
    const lastName = String(item?.last_name ?? '').trim();

    return {
        id: clientId,
        customer_id: customerId,
        first_name: firstName,
        last_name: lastName,
        displayName: `${lastName}, ${firstName}`.replace(/^,\s*/, '').trim(),
        mobile: String(item?.mobile ?? '').trim(),
        pin: String(item?.pin ?? '').trim(),
    };
};

const ModalPodEdit = ({closeModal, onSubmit, podEdit, isNewPod, allPodTypes, allCustomers, thisCustomerFacilities, isReadOnly = false}) => {
    const tokenPayload = getCurrentUser() || {};
    const tokenUserType = tokenPayload.user_type;
    const tokenUserTypeDescription = String(tokenPayload?.user_type_description ?? tokenPayload?.description ?? '').trim().toLowerCase();
    const isReadOnlyMode = !!isReadOnly;
    const initialPod = isNewPod
        ? {
            description: '',
            machine_type: 2,
            machine_type_description: 'merican',
            is_authorized: true,
            plain: {},
            encrypted: {},
            customer: 0,
            customer_name: 'Default Customer',
            facility: 0,
            facility_name: 'Default_Facility',
            serial_tag: '',
            installation_date: '',
            vh_key: uuidv4()
        }
        : {
            ...podEdit,
            installation_date: formatDateForInput(podEdit?.installation_date),
        };

    const [podInfo, setPodInfo] = useState(initialPod);
    const [customerSelectDisabled, setCustomerSelectDisabled] = useState(false);
    const [customerFacilities, setCustomerFacilities] = useState(thisCustomerFacilities ?? []);
    const [installationTypes, setInstallationTypes] = useState([]);
    const [errors, setErrors] = useState('');
    const [remoteStatus, setRemoteStatus] = useState(null);
    const [remoteControlClients, setRemoteControlClients] = useState([]);
    const [remoteControlProtocols, setRemoteControlProtocols] = useState([]);
    const [remoteControlForm, setRemoteControlForm] = useState({
        command: 'start_session',
        clientId: '',
        protocolId: '',
        startPaused: false,
    });
    const [remoteControlMessage, setRemoteControlMessage] = useState('');
    const [isSendingRemoteControl, setIsSendingRemoteControl] = useState(false);
    const [isErrorLogsModalOpen, setIsErrorLogsModalOpen] = useState(false);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const emptyRemoteStatusPollCount = useRef(0);
    const remoteControlMessageTimeoutRef = useRef(null);
    const installationDateFromSerialTag = useMemo(() => formatInstallationDateFromSerialTag(podInfo?.serial_tag), [podInfo?.serial_tag]);

    const selectedPodTypeValue = useMemo(() => {
        const directTypeId = podInfo?.machine_type_id ?? podInfo?.machine_type;
        if (directTypeId !== undefined && directTypeId !== null && directTypeId !== '') {
            return String(directTypeId);
        }

        const podTypeDescription = String(podInfo?.machine_type_description ?? '').trim().toLowerCase();
        if (!podTypeDescription) {
            return '';
        }

        const matchedType = (allPodTypes ?? []).find((podType) =>
            String(podType?.description ?? '').trim().toLowerCase() === podTypeDescription
        );
        const matchedTypeId = matchedType?.id ?? matchedType?.machine_type_id;
        return matchedTypeId !== undefined && matchedTypeId !== null ? String(matchedTypeId) : '';
    }, [allPodTypes, podInfo]);

    const selectedInstallationTypeValue = useMemo(() => {
        const directInstallationTypeId = podInfo?.installation_type_id ?? podInfo?.installation_type;
        if (directInstallationTypeId !== undefined && directInstallationTypeId !== null && directInstallationTypeId !== '') {
            return String(directInstallationTypeId);
        }

        const installationTypeDescription = String(podInfo?.installation_type_description ?? '').trim().toLowerCase();
        if (!installationTypeDescription) {
            return '';
        }

        const matchedType = (installationTypes ?? []).find((installationType) =>
            String(installationType?.description ?? '').trim().toLowerCase() === installationTypeDescription
        );
        const matchedTypeId = matchedType?.id ?? matchedType?.installation_type_id;
        return matchedTypeId !== undefined && matchedTypeId !== null ? String(matchedTypeId) : '';
    }, [installationTypes, podInfo]);

    const selectedRemoteClient = useMemo(() => {
        return remoteControlClients.find((client) => String(client.id) === String(remoteControlForm.clientId)) ?? null;
    }, [remoteControlClients, remoteControlForm.clientId]);

    const resolvedCustomerId = useMemo(() => {
        const customerId = podInfo?.customer_id ?? podInfo?.customer;
        return Number.isFinite(Number(customerId)) ? Number(customerId) : 0;
    }, [podInfo]);

    const isSimpleRemoteControlCommand = useMemo(() => {
        return SIMPLE_REMOTE_CONTROL_COMMANDS.has(remoteControlForm.command);
    }, [remoteControlForm.command]);

    const isStartSessionRemoteControlCommand = useMemo(() => {
        return remoteControlForm.command === 'start_session';
    }, [remoteControlForm.command]);

    const isCreatorUser = useMemo(() => {
        return Number(tokenUserType) === 10 || tokenUserTypeDescription.includes('creator');
    }, [tokenUserType, tokenUserTypeDescription]);

    useEffect(() => {
        return () => {
            if (remoteControlMessageTimeoutRef.current) {
                clearTimeout(remoteControlMessageTimeoutRef.current);
            }
        };
    }, []);

    const clearRemoteControlMessage = () => {
        if (remoteControlMessageTimeoutRef.current) {
            clearTimeout(remoteControlMessageTimeoutRef.current);
            remoteControlMessageTimeoutRef.current = null;
        }

        setRemoteControlMessage('');
    };

    const showRemoteControlMessage = (message) => {
        if (remoteControlMessageTimeoutRef.current) {
            clearTimeout(remoteControlMessageTimeoutRef.current);
        }

        setRemoteControlMessage(message);
        remoteControlMessageTimeoutRef.current = setTimeout(() => {
            setRemoteControlMessage('');
            remoteControlMessageTimeoutRef.current = null;
        }, REMOTE_CONTROL_MESSAGE_TIMEOUT_MS);
    };

    useEffect(() => {
        if (tokenUserType < 3) {
            setCustomerSelectDisabled(true);
        }
    }, [tokenUserType]);

    useEffect(() => {
        const fetchInstallationTypes = async () => {
            try {
                const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/installation_types/', {
                    method: 'GET',
                    headers: {
                        machine: '2',
                        'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
                }

                const data = await response.json();
                const formattedTypes = (Array.isArray(data) ? data : []).map((installationType) => ({
                    id: installationType?.id ?? installationType?.installation_type_id,
                    description: installationType?.description ?? installationType?.installation_type ?? '',
                })).filter((installationType) => installationType.id !== undefined && installationType.id !== null && installationType.description);

                setInstallationTypes(formattedTypes);
            } catch (error) {
                console.error(error);
                setInstallationTypes([]);
            }
        };

        fetchInstallationTypes();
    }, []);

    useEffect(() => {
        if (isNewPod || !podInfo?.id) {
            emptyRemoteStatusPollCount.current = 0;
            setRemoteStatus(null);
            return undefined;
        }

        let isMounted = true;

        const handleNoStatusData = (previousStatus) => {
            emptyRemoteStatusPollCount.current += 1;
            if (emptyRemoteStatusPollCount.current >= REMOTE_STATUS_EMPTY_POLL_LIMIT) {
                return null;
            }

            return previousStatus;
        };

        const fetchRemoteStatus = async () => {
            try {
                const response = await fetch(
                    process.env.REACT_APP_API_URL_BASE + '/pod_status/valhalla-pod-status-' + podInfo.id,
                    {
                        method: 'GET',
                        headers: {
                            machine: '2',
                            'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
                }

                const data = await response.json();
                const nextStatus = normalizeRemoteStatus(data);
                if (isMounted) {
                    setRemoteStatus((previousStatus) => {
                        if (nextStatus) {
                            if (!isRemoteStatusLater(nextStatus, previousStatus)) {
                                return previousStatus;
                            }

                            emptyRemoteStatusPollCount.current = 0;
                            return nextStatus;
                        }

                        return handleNoStatusData(previousStatus);
                    });
                }
            } catch (error) {
                if (isMounted) {
                    setRemoteStatus((previousStatus) => handleNoStatusData(previousStatus));
                }
                console.error(error);
            }
        };

        fetchRemoteStatus();
        const remoteStatusInterval = setInterval(fetchRemoteStatus, REMOTE_STATUS_POLL_MS);

        return () => {
            isMounted = false;
            clearInterval(remoteStatusInterval);
        };
    }, [isNewPod, podInfo?.id]);

    useEffect(() => {
        if (!isCreatorUser && (!resolvedCustomerId || resolvedCustomerId <= 0)) {
            setRemoteControlClients([]);
            setRemoteControlProtocols([]);
            setRemoteControlForm((previousForm) => ({
                ...previousForm,
                clientId: '',
                protocolId: '',
            }));
            return undefined;
        }

        let isMounted = true;

        const fetchRemoteControlOptions = async () => {
            try {
                const requestHeaders = {
                    machine: '2',
                    'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                };

                const clientsUrl = isCreatorUser
                    ? process.env.REACT_APP_API_URL_BASE + '/clients/view'
                    : process.env.REACT_APP_API_URL_BASE + '/clients/customer/' + resolvedCustomerId + '/view';
                const protocolsUrl = isCreatorUser
                    ? process.env.REACT_APP_API_URL_BASE + '/protocols/view'
                    : process.env.REACT_APP_API_URL_BASE + '/customer_protocols/customer_list/' + resolvedCustomerId;

                const [clientsResponse, protocolsResponse] = await Promise.all([
                    fetch(clientsUrl, {
                        method: 'GET',
                        headers: requestHeaders,
                    }),
                    fetch(protocolsUrl, {
                        method: 'GET',
                        headers: requestHeaders,
                    }),
                ]);

                if (!clientsResponse.ok) {
                    throw new Error(`Fetch error - ${clientsResponse.url} - Status: ${clientsResponse.status}`);
                }

                if (!protocolsResponse.ok) {
                    throw new Error(`Fetch error - ${protocolsResponse.url} - Status: ${protocolsResponse.status}`);
                }

                const [clientsData, protocolsData] = await Promise.all([
                    clientsResponse.json(),
                    protocolsResponse.json(),
                ]);

                if (!isMounted) {
                    return;
                }

                const normalizedClients = (Array.isArray(clientsData) ? clientsData : [])
                    .map((item) => buildClientOption(item))
                    .filter((client) => client.id > 0 && client.displayName && client.mobile && client.pin);
                const normalizedProtocols = (Array.isArray(protocolsData) ? protocolsData : [])
                    .map((item) => buildProtocolOption(item))
                    .filter((protocol) => protocol.id > 0 && protocol.description);

                setRemoteControlClients(normalizedClients);
                setRemoteControlProtocols(normalizedProtocols);
                setRemoteControlForm((previousForm) => ({
                    ...previousForm,
                    clientId: normalizedClients.some((client) => String(client.id) === String(previousForm.clientId))
                        ? previousForm.clientId
                        : String(normalizedClients[0]?.id ?? ''),
                    protocolId: normalizedProtocols.some((protocol) => String(protocol.id) === String(previousForm.protocolId))
                        ? previousForm.protocolId
                        : String(normalizedProtocols[0]?.id ?? ''),
                }));
            } catch (error) {
                if (isMounted) {
                    setRemoteControlClients([]);
                    setRemoteControlProtocols([]);
                    setRemoteControlMessage('Unable to load remote control options.');
                }
                console.error(error);
            }
        };

        fetchRemoteControlOptions();

        return () => {
            isMounted = false;
        };
    }, [isCreatorUser, resolvedCustomerId]);

    useEffect(() => {
        if (!remoteStatus?.shouldCountdown) {
            return undefined;
        }

        const countdownInterval = setInterval(() => {
            setRemoteStatus((previousStatus) => {
                if (!previousStatus?.shouldCountdown) {
                    return previousStatus;
                }

                const nextRemainingTime = Math.max(0, Number(previousStatus.remainingTimeSec ?? 0) - 1);
                if (nextRemainingTime === Number(previousStatus.remainingTimeSec ?? 0)) {
                    return previousStatus;
                }

                return {
                    ...previousStatus,
                    remainingTimeSec: nextRemainingTime,
                };
            });
        }, 1000);

        return () => clearInterval(countdownInterval);
    }, [remoteStatus?.shouldCountdown]);

    const apiGetCustomerFacilities = async (customer_id) => {
        fetch(process.env.REACT_APP_API_URL_BASE + '/facilities/customer/' + customer_id + '/view',
            {
                method: 'GET',
                headers: {
                    machine: '2',
                    'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            })
            .then((res) => res.json())
            .then((data) => {
                setCustomerFacilities(Array.isArray(data) ? data : []);
            })
            .catch((error) => console.error(error));
    };

    const apiPatchPodInfo = async (podId, patchPodInfo) => {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/machines/machine/' + podId,
                {
                    method: 'PATCH',
                    headers: {
                        machine: '2',
                        'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                        'content-type': 'application/json'
                    },
                    body: JSON.stringify(patchPodInfo),
                });
            if (!response.ok) {
                throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
            }
            await response.json();
        } catch (error) {
            console.error(error);
        }
    };

    const apiPostNewPod = async (patchPodInfo) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/machines/',
            {
                method: 'POST',
                headers: {
                    machine: '2',
                    'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json'
                },
                body: JSON.stringify(patchPodInfo),
            });
        await response.json();
        if (!response.ok) {
            console.log('Failed to update the resource');
            console.log(response);
        }
        return true;
    };

    const validateForm = async () => {
        const patchPodInfo = {...podInfo};

        if (patchPodInfo.facility_name === '' && customerFacilities.length > 0) {
            patchPodInfo.facility_name = customerFacilities[0].facility_name;
            patchPodInfo.facility_id = customerFacilities[0].id;
            patchPodInfo.facility = customerFacilities[0].id;
        }

        if (patchPodInfo.description && patchPodInfo.machine_type_description && patchPodInfo.customer_name && patchPodInfo.facility_name) {
            setErrors('');
            const podId = patchPodInfo.id;
            const keysToRemove = [
                'id',
                'created_on',
                'last_updated',
                'software_version',
                'customer_name',
                'customer_id',
                'facility_name',
                'facility_id',
                'machine_type_description',
                'installation_type_description'
            ];
            keysToRemove.forEach((key) => delete patchPodInfo[key]);

            if (patchPodInfo.machine_type_id) {
                patchPodInfo.machine_type = patchPodInfo.machine_type_id;
            }

            if (patchPodInfo.installation_type_id) {
                patchPodInfo.installation_type = patchPodInfo.installation_type_id;
            }

            if (isNewPod) {
                await apiPostNewPod(patchPodInfo);
            } else {
                await apiPatchPodInfo(podId, patchPodInfo);
            }

            return true;
        }

        const errorFields = [];
        for (const [key, value] of Object.entries(podInfo)) {
            if (!value) {
                errorFields.push(key);
            }
        }
        setErrors(errorFields.join(', '));
        return false;
    };

    const handleChange = (e) => {
        setPodInfo({
            ...podInfo,
            [e.target.name]: e.target.value
        });
    };

    const handleTypeChange = (e) => {
        const selectedTypeId = Number(e.target.value);
        const selectedType = (allPodTypes ?? []).find((podType) => {
            const podTypeId = podType?.id ?? podType?.machine_type_id;
            return Number(podTypeId) === selectedTypeId;
        });

        if (!selectedType) {
            return;
        }

        const resolvedTypeId = selectedType?.id ?? selectedType?.machine_type_id;
        setPodInfo({
            ...podInfo,
            machine_type_description: selectedType?.description ?? '',
            machine_type: resolvedTypeId,
            machine_type_id: resolvedTypeId
        });
    };

    const handleInstallationTypeChange = (e) => {
        const selectedInstallationTypeId = Number(e.target.value);
        const selectedInstallationType = (installationTypes ?? []).find((installationType) => {
            const installationTypeId = installationType?.id ?? installationType?.installation_type_id;
            return Number(installationTypeId) === selectedInstallationTypeId;
        });

        setPodInfo({
            ...podInfo,
            installation_type_description: selectedInstallationType?.description ?? '',
            installation_type: selectedInstallationTypeId,
            installation_type_id: selectedInstallationTypeId,
        });
    };

    const handleCustomerChange = (e) => {
        const selectedCustomerId = Number(e.target.value);
        const selectedCustomer = (allCustomers ?? []).find((customer) => {
            const customerId = customer.id ?? customer.customer_id;
            return Number(customerId) === selectedCustomerId;
        });

        setPodInfo({
            ...podInfo,
            customer_name: selectedCustomer?.customer_name ?? '',
            customer_id: selectedCustomerId,
            customer: selectedCustomerId,
            facility_name: '',
            facility_id: null,
            facility: null
        });

        if (selectedCustomerId !== undefined && selectedCustomerId !== null) {
            apiGetCustomerFacilities(selectedCustomerId);
        }
    };

    const handleFacilityChange = (e) => {
        const selectedFacilityId = Number(e.target.value);
        const selectedFacility = customerFacilities.find((facility) => {
            const facilityId = facility.id ?? facility.facility_id;
            return Number(facilityId) === selectedFacilityId;
        });

        setPodInfo({
            ...podInfo,
            facility_name: selectedFacility?.facility_name ?? '',
            facility_id: selectedFacilityId,
            facility: selectedFacilityId
        });
    };

    const handleCheckChange = (e) => {
        setPodInfo({
            ...podInfo,
            [e.target.name]: e.target.checked
        });
    };

    const handleRemoteControlChange = (e) => {
        const { name, value, type, checked } = e.target;
        setRemoteControlForm((previousForm) => ({
            ...previousForm,
            [name]: type === 'checkbox' ? checked : value,
        }));
        clearRemoteControlMessage();
    };

    const handleRemoteControlSubmit = async (e) => {
        e.preventDefault();

        if (!podInfo?.id) {
            showRemoteControlMessage('Remote control is only available for saved pods.');
            return;
        }

        if (!isSimpleRemoteControlCommand && !selectedRemoteClient) {
            showRemoteControlMessage('Select a client with both mobile and PIN set.');
            return;
        }

        if (!isSimpleRemoteControlCommand && !remoteControlForm.protocolId) {
            showRemoteControlMessage('Select a protocol.');
            return;
        }

        const payload = isSimpleRemoteControlCommand
            ? {
                command: remoteControlForm.command,
            }
            : {
                command: remoteControlForm.command,
                client_mobile: selectedRemoteClient.mobile,
                client_pin: selectedRemoteClient.pin,
                customer_id: selectedRemoteClient.customer_id,
                protocol_id: Number(remoteControlForm.protocolId),
                start_paused: Boolean(remoteControlForm.startPaused),
            };

        setIsSendingRemoteControl(true);
    clearRemoteControlMessage();

        try {
            const streamName = 'valhalla-pod-control-' + podInfo.id;
            const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/pod_control/' + streamName, {
                method: 'POST',
                headers: {
                    machine: '2',
                    'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
            }

            showRemoteControlMessage('Remote control command sent.');
        } catch (error) {
            console.error(error);
            showRemoteControlMessage('Unable to send remote control command.');
        } finally {
            setIsSendingRemoteControl(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isValidated = await validateForm();
        if (!isValidated) return;

        onSubmit(podInfo);
        closeModal();
    };

    const handleCancel = (e) => {
        e.preventDefault();
        closeModal();
    };

    return (
        <div className='modal-container pod-modal-container'>
            <div className='modal-edit pod-modal-edit'>
                <button className='modal-close-btn' onClick={handleCancel} aria-label='Close pod details modal'>X</button>
                <form>
                    <div className='client-card-grid'>
                        <section className='client-card'>
                            <div className='client-card-header'>
                                <h3>Pod Details</h3>
                            </div>
                            <div className='client-fields-grid two-col'>
                                <div className='wide id-row'>
                                    <div className='id-meta'>
                                        <label htmlFor='pod_id' className='id-value'>ID: {isNewPod ? 'New Pod' : (podInfo.id ?? '')}</label>
                                        {installationDateFromSerialTag && (
                                            <span className='id-secondary-value'>Installation Date: {installationDateFromSerialTag}</span>
                                        )}
                                    </div>
                                    <div className='id-active-toggle'>
                                        <input id='is_authorized' name='is_authorized' type='checkbox' value={podInfo.is_authorized} onChange={handleCheckChange} checked={podInfo.is_authorized} disabled={isReadOnlyMode}/>
                                        <label htmlFor='is_authorized'>Authorized</label>
                                    </div>
                                </div>

                                <div className='form-group'>
                                    <label htmlFor='description'>Pod Description*</label>
                                    <input name='description' value={podInfo.description} onChange={handleChange} readOnly={isReadOnlyMode}/>
                                </div>

                                <div className='form-group'>
                                    <label htmlFor='pod_type'>Pod Type*</label>
                                    <select name='machine_type' value={selectedPodTypeValue} onChange={handleTypeChange} disabled={isReadOnlyMode}>
                                        {(allPodTypes ?? []).map((pod_type, index) => {
                                            const podTypeId = pod_type.id ?? pod_type.machine_type_id;
                                            return (
                                                <option key={podTypeId ?? index} value={podTypeId}>
                                                    {pod_type.description}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>

                                <div className='form-group'>
                                    <label htmlFor='installation_type'>Installation Type</label>
                                    <select
                                        name='installation_type'
                                        value={selectedInstallationTypeValue}
                                        onChange={handleInstallationTypeChange}
                                        disabled={isReadOnlyMode}
                                    >
                                        <option value=''>Select Installation Type</option>
                                        {(installationTypes ?? []).map((installationType, index) => {
                                            const installationTypeId = installationType.id ?? installationType.installation_type_id;
                                            return (
                                                <option key={installationTypeId ?? index} value={installationTypeId}>
                                                    {installationType.description}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>

                                <div className='form-group'>
                                    <label htmlFor='serial_tag'>Serial Tag</label>
                                    <input
                                        type='text'
                                        name='serial_tag'
                                        value={podInfo.serial_tag ?? ''}
                                        onChange={handleChange}
                                        readOnly={isReadOnlyMode}
                                    />
                                </div>

                                <div className='form-group'>
                                    <label htmlFor='customer'>Customer*</label>
                                    <select
                                        name='customer'
                                        value={podInfo.customer_id ?? podInfo.customer ?? ''}
                                        onChange={handleCustomerChange}
                                        disabled={customerSelectDisabled || isReadOnlyMode}
                                    >
                                        {(allCustomers ?? []).map((customer, index) => {
                                            const customerId = customer.id ?? customer.customer_id;
                                            return (
                                                <option key={customerId ?? index} value={customerId} customer_id={customerId}>
                                                    {customer.customer_name}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>

                                <div className='form-group'>
                                    <label htmlFor='facility'>Facility*</label>
                                    <select
                                        name='facility'
                                        value={podInfo.facility_id ?? podInfo.facility ?? ''}
                                        onChange={handleFacilityChange}
                                        disabled={isReadOnlyMode}
                                    >
                                        {customerFacilities.map((facility, index) => {
                                            const facilityId = facility.id ?? facility.facility_id;
                                            return (
                                                <option key={facilityId ?? index} value={facilityId} facility_id={facilityId}>
                                                    {facility.facility_name}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        </section>

                        <section className='client-card remote-status-card'>
                            <div className='client-card-header remote-status-card-header'>
                                <h3>Current Status</h3>

                                {!!remoteStatus?.activeFlags?.length && (
                                    <div className='remote-status-flags'>
                                        {remoteStatus.activeFlags.map((flag) => (
                                            <span key={flag} className={`remote-status-flag ${getRemoteStatusFlagClassName(flag)}`.trim()}>{flag}</span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className='client-fields-grid one-col'>
                                {!remoteStatus?.activeFlags?.length && (
                                    <div className='remote-status-empty'>Trying to Connect...</div>
                                )}

                                {remoteStatus?.showClient && (
                                    <div className='remote-status-detail-list'>
                                        <div className='remote-status-detail-row'>
                                            <span className='remote-status-detail-label'>Client Name</span>
                                            <span className='remote-status-detail-value'>{remoteStatus.clientName}</span>
                                        </div>
                                    </div>
                                )}

                                {remoteStatus?.showProtocolName && (
                                    <div className='remote-status-detail-row'>
                                        <span className='remote-status-detail-label'>Protocol Name</span>
                                        <span className='remote-status-detail-value'>{remoteStatus.protocolName}</span>
                                    </div>
                                )}

                                {remoteStatus?.showRemainingTime && (
                                    <div className='remote-status-detail-row'>
                                        <span className='remote-status-detail-label'>Time Remaining</span>
                                        <span className='remote-status-detail-value remote-status-time'>{formatSecondsToMinutes(remoteStatus.remainingTimeSec)}</span>
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className='client-card remote-control-card'>
                            <div className='client-card-header'>
                                <h3>Pod Control</h3>
                            </div>

                            <div className='client-fields-grid two-col'>
                                <div className='form-group remote-control-command-field'>
                                    <label htmlFor='command'>Command</label>
                                    <select name='command' value={remoteControlForm.command} onChange={handleRemoteControlChange}>
                                        <option value='start_session'>Start Session</option>
                                        <option value='start'>Start / Resume</option>
                                        <option value='stop'>Stop</option>
                                        <option value='pause'>Pause</option>
                                        <option value='logout'>Logout</option>
                                    </select>
                                </div>

                                <div className={`form-group remote-control-protocol-field ${!isStartSessionRemoteControlCommand ? 'remote-control-field-disabled' : ''}`.trim()}>
                                    <label htmlFor='protocolId'>Protocol</label>
                                    <select name='protocolId' value={remoteControlForm.protocolId} onChange={handleRemoteControlChange} disabled={!isStartSessionRemoteControlCommand || remoteControlProtocols.length === 0}>
                                        {remoteControlProtocols.length === 0 && <option value=''>No protocols available</option>}
                                        {remoteControlProtocols.map((protocol) => (
                                            <option key={protocol.id} value={protocol.id}>{protocol.dropdownLabel}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={`form-group wide ${!isStartSessionRemoteControlCommand ? 'remote-control-field-disabled' : ''}`.trim()}>
                                    <label htmlFor='clientId'>Client</label>
                                    <select name='clientId' value={remoteControlForm.clientId} onChange={handleRemoteControlChange} disabled={!isStartSessionRemoteControlCommand || remoteControlClients.length === 0}>
                                        {remoteControlClients.length === 0 && <option value=''>No eligible clients available</option>}
                                        {remoteControlClients.map((client) => (
                                            <option key={client.id} value={client.id}>{client.displayName}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={`form-group toggle-field wide remote-control-toggle ${!isStartSessionRemoteControlCommand ? 'remote-control-field-disabled' : ''}`.trim()}>
                                    <input id='startPaused' name='startPaused' type='checkbox' checked={remoteControlForm.startPaused} onChange={handleRemoteControlChange} disabled={!isStartSessionRemoteControlCommand} />
                                    <label htmlFor='startPaused'>Start Paused</label>
                                </div>
                            </div>

                            {remoteControlMessage && <div className='remote-control-message'>{remoteControlMessage}</div>}

                            <div className='remote-control-actions'>
                                <button
                                    type='button'
                                    className='btn'
                                    onClick={handleRemoteControlSubmit}
                                    disabled={isSendingRemoteControl || !podInfo?.id || (!isSimpleRemoteControlCommand && (!selectedRemoteClient || !remoteControlForm.protocolId))}
                                >
                                    {isSendingRemoteControl ? 'Sending...' : 'Send Command'}
                                </button>
                            </div>
                        </section>

                        {isReadOnlyMode && isCreatorUser && (
                            <>
                                <div className='remote-control-link-row'>
                                    <button type='button' className='remote-control-link-btn' onClick={() => setIsErrorLogsModalOpen(true)}>
                                        View Error Log
                                    </button>
                                </div>

                                <div className='remote-control-link-row'>
                                    <button type='button' className='remote-control-link-btn' onClick={() => setIsConfigModalOpen(true)}>
                                        Edit Config
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {!isReadOnlyMode && errors && <div className='error'>{`Please include: ${errors}`}</div>}
                    <div className='client-actions-row'>
                        <button type='button' className='btn btn-cancel' onClick={handleCancel}>{isReadOnlyMode ? 'Close' : 'Cancel'}</button>
                        {!isReadOnlyMode && <button type='submit' className='btn' onClick={handleSubmit}>Save</button>}
                    </div>
                </form>
            </div>

            {isErrorLogsModalOpen && (
                <ModalPodErrorLogs
                    closeModal={() => setIsErrorLogsModalOpen(false)}
                    machineId={podInfo?.id}
                    podInfo={podInfo}
                />
            )}

            {isConfigModalOpen && (
                <ModalPodConfig
                    closeModal={() => setIsConfigModalOpen(false)}
                    machineId={podInfo?.id}
                    podInfo={podInfo}
                />
            )}
        </div>
    )
}

export default ModalPodEdit


